// Default React import: the Jest babel config uses the classic JSX runtime
import React, { useEffect, useRef, useState } from "react";
import { Chip } from "@mui/material";
import { Icon } from "semantic-ui-react";
import {
  platformChipSx,
  type ListingPlatform,
} from "../../../pages/tables/pricetool/listingPlatform";

interface IdentifierLinkProps {
  value: string | null | undefined;
  /** The value's product page, opened in a new tab. Leave out for ids
   *  without a public page (MSKU, Walmart seller SKU). */
  href?: string;
  /** What the value is, for the copy button's label: "ASIN", "Walsku"… */
  label: string;
  /** Marketplace the id belongs to. Set, the value renders as a chip in
   *  that platform's colours, like the Listings grid's Platform column. */
  platform?: ListingPlatform;
}

const COPIED_MS = 1500;

/**
 * An identifier with a copy button, rendered as a link when it has a page.
 * Shows "—" for an empty value.
 */
export function IdentifierLink({
  value,
  href,
  label,
  platform,
}: IdentifierLinkProps) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    []
  );

  const text = value?.trim();
  if (!text) return <span>—</span>;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      return; // No clipboard access (plain http, permission denied)
    }
    setCopied(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), COPIED_MS);
  };

  const renderValue = () => {
    if (platform) {
      // The chip itself is the link, so it stays one element for a11y
      return href ? (
        <Chip
          label={text}
          size="small"
          sx={platformChipSx(platform)}
          component="a"
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          clickable
        />
      ) : (
        <Chip label={text} size="small" sx={platformChipSx(platform)} />
      );
    }
    return href ? (
      <a href={href} target="_blank" rel="noopener noreferrer">
        {text}
      </a>
    ) : (
      <span>{text}</span>
    );
  };

  return (
    <span
      style={{
        whiteSpace: "nowrap",
        display: "inline-flex",
        alignItems: "center",
      }}
      data-testid="identifier-link"
    >
      {renderValue()}
      <Icon
        name="copy outline"
        link
        role="button"
        aria-label={`Copy ${label}`}
        title={`Copy ${label}`}
        style={{ marginLeft: "0.4em" }}
        onClick={copy}
      />
      {copied && (
        <span style={{ color: "#21ba45", fontSize: "0.85em" }}>Copied</span>
      )}
    </span>
  );
}
